// Define all possible routes as const to ensure type safety
export const ROUTES = {
  BASE: "/",
  DASHBOARD: "/dashboard/",
  ACCOUNTS: "/accounts/",
  CMS: "/CMS/",
  TAGGING: "/tagging",
  TAGGING_V2: "/tagging-v2",
  LIVE_TRACKING: "/live-tracking",
  CHALLENGE_MODE: "/challenge-mode",
  EVENT_LOG: "/event-log",
  EVENT_LOG_EDIT: "/event-log/:gameId",
  EVENT_SCHEDULE: "/event-schedule",
  SETTINGS: "/settings/",
  STATS: "/stats/",
  REPORTS: "/reports/",

  MANAGE_SMARTPARKS: "/manage-smartparks",
  MANAGE_COMPLEX: "/manage-smartparks/complex/:complexName",
  MANAGE_FIELD: "/manage-smartparks/complex/:complexName/field/:fieldId",
  NEW_EVENT: "/manage-smartparks/complex/:complexName/field/:fieldId/new-event",
  EDIT_EVENT:
    "/manage-smartparks/complex/:complexName/field/:fieldId/edit-event/:eventId",

  MANAGE_SMARTPARKS_ADMIN: "/manage-smartparks-admin",

  ROSTERS: "rosters/",
  // Do not add trailing slash to also include nested children routes
  PLAYER_BY_ID: "/players/:id",
  LOGIN: "/login/",
  CLAIM_PERMISSIONS: "/claim-permissions/",
  NOT_FOUND: "/404/",
  NO_ACCESS: "/no-access/",
  // Manage — operators, rosters, players, organizations
  MANAGE: "/manage",
  MANAGE_OPERATORS: "/manage/operators",
  MANAGE_ROSTERS: "/manage/rosters",
  MANAGE_PLAYERS: "/manage/players",
  MANAGE_ORGANIZATIONS: "/manage/organizations",
  MANAGE_BILLING: "/manage/billing",
  // Admin Tools — admins, account verification, find CSV
  ADMIN_TOOLS: "/admin-tools",
  ADMIN_TOOLS_ADMINS: "/admin-tools/admins",
  ADMIN_TOOLS_ACCOUNT_VERIFICATION: "/admin-tools/account-verification",
  ADMIN_TOOLS_FIND_CSV: "/admin-tools/find-csv",
} as const;

// Create a type from the values
export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];

// Helper to check if a path starts with a route
export const pathStartsWith = (path: string, route: AppRoute): boolean => {
  if (route === ROUTES.BASE) {
    return path === "/" || path === "";
  }

  // If the route ends with a slash (with or without parameters),
  // only match if path is exactly that route with optional trailing slash (no extra segments).
  if (route.endsWith("/")) {
    const normalizedRoute = route.replace(/\/+$/, ""); // remove trailing slash
    const paramPattern = normalizedRoute
      .replace(/\//g, "\\/")
      .replace(/:([^\\/]+)/g, "[^\\/]+");
    const exactRegex = new RegExp(`^${paramPattern}\\/?$`, "i");
    return exactRegex.test(path);
  }

  // Convert route parameters (e.g. :organizationId) to a regex segment
  const paramPattern = route
    .replace(/\//g, "\\/") // escape slashes
    .replace(/:([^\\/]+)/g, "[^\\/]+"); // replace param like :organizationId

  // Create a regex to match the start of the path (including any child routes)
  // Allow child routes for routes that don't end with trailing slash
  const routeRegex = new RegExp(`^${paramPattern}(?:\\/.*)?$`, "i");

  return routeRegex.test(path);
};
